import React, { useEffect, useState } from 'react';
import { api } from './AdminApp';
import Modal from './Modal';
import Toggle from './Toggle';
import Loader from '../Components/Loader/Loader';

const EMPTY = {
  title: '',
  location: 'Ambala',
  job_type: 'Full Time',
  experience: '',
  description: '',
  display_order: 0,
  is_active: true,
};

const JOB_TYPES = ['Full Time', 'Part Time', 'Contract', 'Internship'];

export default function Careers() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const d = await api('/admin/careers');
      setList(d.careers || []);
      setError('');
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => setEditing({ ...EMPTY, id: 0 });

  const startEdit = (c) =>
    setEditing({
      id: c.id,
      title: c.title || '',
      location: c.location || 'Ambala',
      job_type: c.job_type || 'Full Time',
      experience: c.experience || '',
      description: c.description || '',
      display_order: c.display_order ?? 0,
      is_active: Boolean(c.is_active),
    });

  const save = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await api('/admin/careers/save', {
        method: 'POST',
        body: JSON.stringify(editing),
      });
      setEditing(null);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this job listing?')) return;
    try {
      await api('/admin/careers/delete', {
        method: 'POST',
        body: JSON.stringify({ id }),
      });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const toggle = async (id) => {
    try {
      await api('/admin/careers/toggle', {
        method: 'POST',
        body: JSON.stringify({ id }),
      });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const set = (k) => (e) =>
    setEditing({
      ...editing,
      [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    });

  const close = () => setEditing(null);

  return (
    <div>
      <div className="admin-page-head">
        <h1 className="admin-page-title">Careers</h1>
        <button className="admin-btn admin-btn-primary" onClick={startNew}>
          + Add job listing
        </button>
      </div>
      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      {editing && (
        <Modal
          title={editing.id ? `Edit job #${editing.id}` : 'New job listing'}
          onClose={close}
        >
          <form className="admin-form" onSubmit={save}>
            <div className="admin-form-grid">
              <label>
                Job title *
                <input value={editing.title} onChange={set('title')} required />
              </label>
              <label>
                Location
                <input value={editing.location} onChange={set('location')} />
              </label>
              <label>
                Job type
                <select value={editing.job_type} onChange={set('job_type')}>
                  {JOB_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Experience (e.g. 1-3 Years)
                <input value={editing.experience} onChange={set('experience')} />
              </label>
              <label>
                Display order
                <input type="number" value={editing.display_order} onChange={set('display_order')} />
              </label>
              <label style={{ gridColumn: '1 / -1' }}>
                Description
                <textarea rows={4} value={editing.description} onChange={set('description')} />
              </label>
              <div className="admin-form-checks" style={{ gridColumn: '1 / -1' }}>
                <label>
                  <input type="checkbox" checked={editing.is_active} onChange={set('is_active')} />
                  Visible on site
                </label>
              </div>
              <div className="admin-form-actions" style={{ gridColumn: '1 / -1' }}>
                <button type="submit" className="admin-btn admin-btn-primary">
                  Save
                </button>
                <button type="button" className="admin-btn" onClick={close}>
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </Modal>
      )}

      {loading ? (
        <Loader inline status="Loading job listings" />
      ) : list.length === 0 ? (
        <p className="admin-muted">No job listings yet.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Title</th>
              <th>Location</th>
              <th>Type</th>
              <th>Experience</th>
              <th>Order</th>
              <th>Visible</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id}>
                <td>{c.id}</td>
                <td>
                  <strong>{c.title}</strong>
                  <div className="admin-muted" style={{ fontSize: 12, maxWidth: 320 }}>
                    {c.description && c.description.length > 80
                      ? `${c.description.slice(0, 80)}…`
                      : c.description}
                  </div>
                </td>
                <td>{c.location}</td>
                <td>{c.job_type}</td>
                <td>{c.experience || '—'}</td>
                <td>{c.display_order}</td>
                <td>
                  <Toggle checked={c.is_active} onChange={() => toggle(c.id)} />
                </td>
                <td className="admin-actions">
                  <button className="admin-btn admin-btn-sm" onClick={() => startEdit(c)}>
                    Edit
                  </button>
                  <button
                    className="admin-btn admin-btn-sm admin-btn-danger"
                    onClick={() => remove(c.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
